import React, { Component } from 'react';
import { HTTPService } from '../../services/httpService';
import { getLocationName, ALL_LOCATIONS_ID, ALL_DEALERSHIPS_ID } from '../../util/locationUtils';
import DealerGroupService from '../../services/dealerGroupService';

const API = new HTTPService();

class CompanyLocationSelector extends Component {
  state = {
    open: false,
    companies: [],
    companyLocationsMap: {}, // { installerId: locations[] } for non-current companies
    dealerships: [], // For dealer_group users
  };

  async componentDidMount() {
    if (this.props.userId) {
      await this.loadCompanies();
    }
    // Dealer group: load dealerships for the selector
    if (this.isDealerGroup() && this.props.groupId) {
      this.loadDealerships();
    }
  }

  componentDidUpdate(prevProps) {
    // If current company's locations change via props, no extra work needed —
    // we always read current company locations from props, not state.
  }

  isDealer = () => {
    const { userType } = this.props;
    return userType === 'dealer' || userType === 'dealer_group';
  };

  isDealerGroup = () => {
    return this.props.userType === 'dealer_group';
  };

  loadDealerships = async () => {
    try {
      console.log('[CompanyLocationSelector] loading dealerships for groupId:', this.props.groupId);
      const dealerships = await DealerGroupService.getDealerships(this.props.groupId);
      console.log('[CompanyLocationSelector] dealerships loaded:', dealerships);
      this.setState({ dealerships });
    } catch (err) {
      console.error('CompanyLocationSelector: failed to load dealerships', err);
    }
  };

  loadCompanies = async () => {
    try {
      const { data: rawCompanies } = await API.get(`/userAccess/${this.props.userId}`);
      // Deduplicate — endpoint may return one row per location assignment
      const seen = new Set();
      const companies = rawCompanies.filter(c => {
        if (seen.has(c.Installer_id)) return false;
        seen.add(c.Installer_id);
        return true;
      });
      this.setState({ companies });

      // For multi-company users, pre-fetch locations for other companies
      // Dealers don't have locations — skip for dealer/dealer_group
      if (companies.length >= 2 && !this.isDealer()) {
        this.loadOtherCompanyLocations(companies);
      }
    } catch (err) {
      console.error('CompanyLocationSelector: failed to load companies', err);
    }
  };

  loadOtherCompanyLocations = async (companies) => {
    const currentId = this.getCurrentCompanyId();

    const otherCompanies = companies.filter(c => String(c.Installer_id) !== String(currentId));
    if (otherCompanies.length === 0) return;

    const results = await Promise.all(
      otherCompanies.map(c =>
        API.get(`/installers/${c.Installer_id}/locations`)
          .then(res => ({ id: c.Installer_id, locations: res.data || [] }))
          .catch(() => ({ id: c.Installer_id, locations: [] }))
      )
    );

    const map = {};
    results.forEach(r => { map[r.id] = r.locations; });
    this.setState({ companyLocationsMap: map });
  };

  getCurrentCompanyId = () => {
    try {
      const company = JSON.parse(localStorage.getItem('companyAPI'));
      return company?.Installer_id || null;
    } catch {
      return null;
    }
  };

  getCurrentCompany = (companies = this.state.companies) => {
    const currentId = this.getCurrentCompanyId();
    if (currentId) {
      return companies.find(c => c.Installer_id === currentId) || null;
    }
    // Fallback to name match
    return companies.find(c => c.Installer_Name === this.props.companyName) || null;
  };

  getMode = () => {
    const { userRole } = this.props;
    const { companies } = this.state;

    // Technicians and viewers always see static text
    const staticRoles = ['technician', 'viewer'];
    if (staticRoles.includes(userRole)) {
      return 'static';
    }

    // Dealer group with dealerships — use grouped dropdown
    // Dealerships act as "locations" for the group company
    if (this.isDealerGroup() && this.state.dealerships.length > 1) {
      return 'grouped';
    }

    // Multi-company = grouped dropdown (dealers: company-only, installers: with locations)
    if (companies.length >= 2) {
      return 'grouped';
    }

    // Dealers don't have location sub-selection — single company is always static
    if (this.isDealer()) {
      return 'static';
    }

    // Single company with multiple locations = flat dropdown (installer only)
    const locations = this.props.locations || [];
    if (locations.length > 1) {
      return 'flat';
    }

    // Single company, single/no location
    return 'static';
  };

  getDisplayValue = () => {
    const { companyName, selectedLocationId, selectedDealerId, locations } = this.props;

    // Dealer group: show group name + selected dealership
    if (this.isDealerGroup()) {
      if (!selectedDealerId || selectedDealerId === ALL_DEALERSHIPS_ID) {
        return companyName;
      }
      const dealer = this.state.dealerships.find(
        d => String(d.dealer_id) === String(selectedDealerId)
      );
      if (dealer) {
        return `${companyName} - ${dealer.dealer_name}`;
      }
      return companyName;
    }

    if (!selectedLocationId || selectedLocationId === ALL_LOCATIONS_ID) {
      return companyName;
    }

    const loc = (locations || []).find(
      l => String(l.location_id) === String(selectedLocationId)
    );
    if (loc) {
      return `${companyName} - ${getLocationName(loc)}`;
    }

    return companyName;
  };

  getStaticDisplayValue = () => {
    const { companyName, selectedLocationId, locations } = this.props;
    const locs = locations || [];

    // If there's a selected location, show "Company - Location"
    if (selectedLocationId && selectedLocationId !== ALL_LOCATIONS_ID) {
      const loc = locs.find(l => String(l.location_id) === String(selectedLocationId));
      if (loc) return `${companyName} - ${getLocationName(loc)}`;
    }

    // If there's exactly one location, show it
    if (locs.length === 1) {
      return `${companyName} - ${getLocationName(locs[0])}`;
    }

    return companyName;
  };

  toggleOpen = () => {
    this.setState(prev => ({ open: !prev.open }));
  };

  close = () => {
    this.setState({ open: false });
  };

  // Single handler for all selections — reads localStorage to decide action
  handleSelection = (installerId, locationId = null) => {
    this.close();

    // Read current installer + location from localStorage (source of truth)
    let currentInstallerId = null;
    let currentLocationId = null;
    try {
      const company = JSON.parse(localStorage.getItem('companyAPI'));
      currentInstallerId = company?.Installer_id;
      currentLocationId = company?.selectedLocationId;
    } catch { /* no-op */ }

    const targetLocationId = locationId || ALL_LOCATIONS_ID;
    const isSameCompany = String(installerId) === String(currentInstallerId);
    const isSameLocation = String(targetLocationId) === String(currentLocationId);

    if (isSameCompany && isSameLocation) {
      // Already on this exact selection — no-op
      if (this.props.onAfterSelect) this.props.onAfterSelect();
      return;
    }

    if (isSameCompany) {
      // Same company, different location — lightweight state update
      this.props.onLocationChange(targetLocationId);
    } else {
      // Different company — full switch with optional target location
      this.props.onSwitch(installerId, this.props.userType, locationId);
    }

    if (this.props.onAfterSelect) this.props.onAfterSelect();
  };

  // Build the list of companies with their locations for the grouped dropdown
  getCompaniesWithLocations = () => {
    const { companies, companyLocationsMap, dealerships } = this.state;
    const currentId = this.getCurrentCompanyId();

    // For dealer groups: build the list from standalone dealers + the group entry with dealerships as locations
    if (this.isDealerGroup() && dealerships.length > 0) {
      const standaloneDealers = this.getStandaloneDealers();
      const result = [];

      // Other companies first (same as installer grouped view)
      standaloneDealers.forEach(c => {
        result.push({
          installerId: c.Installer_id,
          name: c.Installer_Name,
          isCurrent: false,
          locations: [],
        });
      });

      // Dealer group as the "current company" with dealerships as locations
      result.push({
        installerId: currentId,
        name: this.props.companyName,
        isCurrent: true,
        locations: dealerships.map(d => ({
          location_id: d.dealer_id,
          location_name: d.dealer_name,
          _isDealership: true,
        })),
      });

      return result;
    }

    return companies.map(c => ({
      installerId: c.Installer_id,
      name: c.Installer_Name,
      isCurrent: String(c.Installer_id) === String(currentId),
      locations: String(c.Installer_id) === String(currentId)
        ? (this.props.locations || [])
        : (companyLocationsMap[c.Installer_id] || []),
    }));
  };

  renderStatic = () => {
    return (
      <span className="navbar-brand mb-0">{this.getStaticDisplayValue()}</span>
    );
  };

  renderFlatDropdown = () => {
    const { companyName, selectedLocationId, locations } = this.props;
    const { open } = this.state;
    const variant = this.props.variant || 'desktop';
    const locs = locations || [];
    const isAllSelected = !selectedLocationId || selectedLocationId === ALL_LOCATIONS_ID;

    return (
      <div className={`cl-selector cl-variant-${variant}`} style={{ position: 'relative', display: 'inline-block' }}>
        <span
          className={`navbar-brand mb-0 cl-toggle${open ? ' open' : ''}`}
          onClick={this.toggleOpen}
        >
          {this.getDisplayValue()}
          <i className="fa-sharp fa-solid fa-chevron-down cl-chevron"></i>
        </span>

        {open && (
          <>
            <div className="cl-backdrop" onClick={this.close} />
            <div className="cl-menu">
              <div
                className={`cl-item${isAllSelected ? ' active' : ''}`}
                onClick={() => this.handleSelection(this.getCurrentCompanyId(), null)}
              >
                All Locations
              </div>
              <div className="cl-divider" />
              {locs.map(loc => {
                const isSelected = String(loc.location_id) === String(selectedLocationId);
                return (
                  <div
                    key={loc.location_id}
                    className={`cl-item${isSelected ? ' active' : ''}`}
                    onClick={() => this.handleSelection(this.getCurrentCompanyId(), loc.location_id)}
                  >
                    {`${companyName} - ${getLocationName(loc)}`}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  renderGroupedDropdown = () => {
    const { selectedLocationId, selectedDealerId } = this.props;
    const { open } = this.state;
    const variant = this.props.variant || 'desktop';
    const companiesWithLocations = this.getCompaniesWithLocations();
    const isDG = this.isDealerGroup();
    const showLocations = isDG || !this.isDealer();
    // For dealer groups, "selected sub-item" is selectedDealerId; for installers, selectedLocationId
    const selectedSubId = isDG ? selectedDealerId : selectedLocationId;
    const allSubId = isDG ? ALL_DEALERSHIPS_ID : ALL_LOCATIONS_ID;

    return (
      <div className={`cl-selector cl-variant-${variant}`} style={{ position: 'relative', display: 'inline-block' }}>
        <span
          className={`navbar-brand mb-0 cl-toggle${open ? ' open' : ''}`}
          onClick={this.toggleOpen}
        >
          {showLocations ? this.getDisplayValue() : this.props.companyName}
          <i className="fa-sharp fa-solid fa-chevron-down cl-chevron"></i>
        </span>

        {open && (
          <>
            <div className="cl-backdrop" onClick={this.close} />
            <div className="cl-menu">
              {companiesWithLocations.map((company, idx) => {
                const isCurrentCompany = company.isCurrent;
                const isCompanyAllSelected = isCurrentCompany &&
                  (!selectedSubId || selectedSubId === allSubId);
                const hasMultipleLocations = showLocations && company.locations.length > 1;
                const hasSingleLocation = showLocations && company.locations.length === 1;

                // Selection handlers differ for dealer group dealerships vs installer locations
                const handleCompanyClick = () => {
                  if (isDG && isCurrentCompany) {
                    this.handleDealershipSelection(ALL_DEALERSHIPS_ID);
                  } else {
                    this.handleSelection(company.installerId, null);
                  }
                };

                const handleSubItemClick = (loc) => {
                  if (loc._isDealership) {
                    this.handleDealershipSelection(loc.location_id);
                  } else {
                    this.handleSelection(company.installerId, loc.location_id);
                  }
                };

                return (
                  <React.Fragment key={company.installerId}>
                    {idx > 0 && <div className="cl-divider" />}

                    {hasSingleLocation ? (
                      // Single location — show just the location entry (no company-level "All Locations")
                      <div
                        className={`cl-item cl-company-item${isCompanyAllSelected || (isCurrentCompany && String(company.locations[0].location_id) === String(selectedSubId)) ? ' active' : ''}`}
                        onClick={() => handleSubItemClick(company.locations[0])}
                      >
                        {`${company.name} - ${getLocationName(company.locations[0])}`}
                      </div>
                    ) : (
                      <>
                        {/* Company-level item (selectable = all locations/dealerships, only option for plain dealers) */}
                        <div
                          className={`cl-item cl-company-item${isCompanyAllSelected ? ' active' : ''}`}
                          onClick={handleCompanyClick}
                        >
                          {company.name}
                        </div>

                        {/* Sub-items: locations (installer) or dealerships (dealer group) */}
                        {hasMultipleLocations && company.locations.map(loc => {
                          const isLocSelected = isCurrentCompany &&
                            String(loc.location_id) === String(selectedSubId);
                          return (
                            <div
                              key={loc.location_id}
                              className={`cl-item cl-location-item${isLocSelected ? ' active' : ''}`}
                              onClick={() => handleSubItemClick(loc)}
                            >
                              {`${company.name} - ${getLocationName(loc)}`}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  handleDealershipSelection = (dealerId) => {
    this.close();

    const currentDealerId = this.props.selectedDealerId;
    if (String(dealerId) === String(currentDealerId)) {
      return;
    }

    // Store in dealerGroupAPI and reload
    try {
      const dgInfo = JSON.parse(localStorage.getItem('dealerGroupAPI') || '{}');
      dgInfo.selectedDealerId = dealerId;
      localStorage.setItem('dealerGroupAPI', JSON.stringify(dgInfo));
    } catch { /* no-op */ }

    window.location.reload();
  };

  // Get standalone dealer companies (not part of the dealer group) from userAccess
  getStandaloneDealers = () => {
    const { companies, dealerships } = this.state;
    if (!companies.length || !dealerships.length) return [];
    // Dealership IDs from the group
    const groupDealerIds = new Set(dealerships.map(d => String(d.dealer_id)));
    // Filter companies that are NOT the current dealer_group company and NOT in the group's dealership list
    const currentId = this.getCurrentCompanyId();
    return companies.filter(c => {
      const cId = String(c.Installer_id);
      return cId !== String(currentId) && !groupDealerIds.has(cId);
    });
  };

  render() {
    const mode = this.getMode();

    switch (mode) {
      case 'grouped':
        return this.renderGroupedDropdown();
      case 'flat':
        return this.renderFlatDropdown();
      case 'static':
      default:
        return this.renderStatic();
    }
  }
}

export default CompanyLocationSelector;
